import { useCallback, useEffect, useState } from "react";
import { UserPlus, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, publicUrl } from "../../lib/api";
import { ROLES, fmtFecha } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { OficialVigente, OwnerOficialCard } from "../../components/OficialVigente";
import { StatusBadge } from "../../components/StatusBadge";
import { Field } from "../../components/Field";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const COMPANY_ROLES = ["owner", "compliance_officer", "operator"];

const RoleSelect = ({ value, onChange, disabled, testId }) => (
  <Select value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger className="w-52 h-9" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>{COMPANY_ROLES.map((r) => <SelectItem key={r} value={r}>{ROLES[r]}</SelectItem>)}</SelectContent>
  </Select>
);

const InviteDialog = ({ open, setOpen, onDone }) => {
  const [f, setF] = useState({ correo: "", nombre: "", rol: "operator" });
  const [link, setLink] = useState(null);
  const close = (v) => { setOpen(v); if (!v) { setLink(null); setF({ correo: "", nombre: "", rol: "operator" }); } };
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/company/invitations", f);
      setLink(publicUrl(data.link_path));
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle>Invitar usuario</DialogTitle>
          <DialogDescription>El enlace es de un solo uso y vence en 48 horas.</DialogDescription>
        </DialogHeader>
        {link ? (
          <div className="space-y-3" data-testid="invite-result">
            <p className="text-sm text-amber-700">Envío por correo SIMULADO en esta fase: copia el enlace y compártelo con la persona invitada.</p>
            <div className="flex gap-2">
              <code className="flex-1 font-mono text-xs bg-slate-100 text-cyan-700 px-3 py-2 rounded break-all" data-testid="invite-link-value">{link}</code>
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(link); toast.success("Enlace copiado"); }} data-testid="invite-link-copy"><Copy size={16} /></Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field id="invite-correo" label="Correo" type="email" required value={f.correo} onChange={(e) => setF({ ...f, correo: e.target.value })} />
            <Field id="invite-nombre" label="Nombre (opcional)" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Rol</Label>
              <RoleSelect value={f.rol} onChange={(v) => setF({ ...f, rol: v })} testId="invite-rol-select" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="invite-submit-button">Generar invitación</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

const UsersTable = ({ users, isOwner, selfId, onPatch }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
    <Table data-testid="users-table">
      <TableHeader><TableRow>{["Nombre", "Correo", "Rol", "TOTP", "Estado", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {users.map((u) => {
          const editable = isOwner && u.id !== selfId;
          return (
            <TableRow key={u.id} data-testid={`user-row-${u.correo}`}>
              <TableCell className="font-medium">{u.nombre}</TableCell>
              <TableCell className="text-sm">{u.correo}</TableCell>
              <TableCell>{editable ? <RoleSelect value={u.rol} onChange={(v) => onPatch(u.id, { rol: v })} testId={`user-role-${u.correo}`} /> : ROLES[u.rol]}</TableCell>
              <TableCell className="text-sm">{u.totp_activo ? "Configurado" : "—"}</TableCell>
              <TableCell><StatusBadge value={u.estado === "activo" ? "ok" : "fallido"} label={u.estado} /></TableCell>
              <TableCell>
                {editable && (
                  <Button size="sm" variant="outline" onClick={() => onPatch(u.id, { estado: u.estado === "activo" ? "inactivo" : "activo" })} data-testid={`user-toggle-${u.correo}`}>
                    {u.estado === "activo" ? "Desactivar" : "Reactivar"}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);

const InvitesTable = ({ items, isOwner, onRevoke }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
    <Table data-testid="invitations-table">
      <TableHeader><TableRow>{["Correo", "Rol", "Creada", "Vence", "Estado", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {items.map((i) => (
          <TableRow key={i.id}>
            <TableCell className="text-sm">{i.correo}</TableCell>
            <TableCell className="text-sm">{ROLES[i.rol]}</TableCell>
            <TableCell className="text-xs">{fmtFecha(i.created_at)}</TableCell>
            <TableCell className="text-xs">{fmtFecha(i.expires_at)}</TableCell>
            <TableCell><StatusBadge value={i.estado === "pendiente" ? "pendiente_verificacion" : i.estado === "usada" ? "ok" : "fallido"} label={i.estado} /></TableCell>
            <TableCell>{isOwner && i.estado === "pendiente" && <Button size="sm" variant="ghost" onClick={() => onRevoke(i.id)} data-testid={`invite-revoke-${i.id}`}>Revocar</Button>}</TableCell>
          </TableRow>
        ))}
        {!items.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin invitaciones</TableCell></TableRow>}
      </TableBody>
    </Table>
  </div>
);

export default function Usuarios() {
  const { me } = useAuth();
  const isOwner = me.usuario.rol === "owner";
  const [users, setUsers] = useState([]);
  const [invs, setInvs] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [u, i] = await Promise.all([api.get("/company/users"), api.get("/company/invitations")]);
    setUsers(u.data.items);
    setInvs(i.data.items);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8">
      <PageHeader overline="Equipo" title="Usuarios">
        {isOwner && <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="invite-open-button"><UserPlus size={16} className="mr-2" /> Invitar usuario</Button>}
      </PageHeader>
      <OficialVigente />
      <OwnerOficialCard />
      <UsersTable users={users} isOwner={isOwner} selfId={me.usuario.id}
        onPatch={(id, body) => act(() => api.patch(`/company/users/${id}`, body), "Usuario actualizado; sus sesiones fueron cerradas")} />
      <h2 className="font-heading text-lg font-semibold">Invitaciones</h2>
      <InvitesTable items={invs} isOwner={isOwner} onRevoke={(id) => act(() => api.delete(`/company/invitations/${id}`), "Invitación revocada")} />
      <InviteDialog open={open} setOpen={setOpen} onDone={load} />
    </div>
  );
}
